// Copyright (c) 2026 Michael F. Kamprath
//
// game-audio.js -- High-level game audio sequences for Learn and Quiz modes.
//
// These helpers wrap the low-level engine `play()` with the specific
// named sequences called out in CONVERSION-PLAN section 8 ("Named
// Sequences as Functions") and docs/QUIZ-MODE-LEGACY-SPEC §5 + §10.2.
// Each helper:
//
//   1. Resolves the audio URL against the active voice set, falling
//      back to the first voice set in the locale (voice-set.js handles
//      the fallback internally).  SFX sequences (pop / plaqueTouch)
//      bypass the voice-set system and resolve to fixed file paths
//      baked in below — they're shared across all themes.
//   2. Awaits playback end, so callers can sequence with `await`.
//   3. Checks a PlaybackToken between steps to bail out cleanly on
//      scene change or rapid state transitions.
//
// The helpers are pure orchestration -- they do NOT touch PixiJS,
// settings, or game state.  Dependencies are injected through
// `createGameAudio` so the whole module is unit-testable without
// Howler or a browser.

import {
  resolvePrompt,
  // resolveNumber is used at tap-time in learn-tap.js; game-audio
  // deliberately sticks to prompt-level helpers so each module has a
  // clear job.
} from './voice-set.js';

// ---------------------------------------------------------------------------
// Theme id -> object slug
// ---------------------------------------------------------------------------
//
// Prompt files are named with the singular object slug
// (canyoucount_sheep, howmany_cow, letscount_horse ...), while the
// game talks about themes in plural form (sheep, cows, horses).
// Sheep is already singular-as-plural.

const THEME_TO_SLUG = Object.freeze({
  sheep: 'sheep',
  cows: 'cow',
  horses: 'horse',
  chicks: 'chick',
  apples: 'apple',
  flowers: 'flower',
});

/**
 * Map a theme ID to the object slug used in prompt filenames.
 *
 * @param {string} themeId
 * @returns {string}
 */
export function themeToObjectSlug(themeId) {
  const slug = THEME_TO_SLUG[themeId];
  if (!slug) {
    // Unknown theme: fall back to the id itself so caller's resolve
    // attempts produce predictable 404s rather than silent misses.
    return themeId;
  }
  return slug;
}

// ---------------------------------------------------------------------------
// Prompt basename pools (per CONVERSION-PLAN section 8)
// ---------------------------------------------------------------------------

/**
 * Learn-intro prompt variants, parameterised by object slug.
 * Called after entrance animation, before the counting interaction begins.
 *
 * These three basenames (canyoucount/howmany/toucheach) ship in every
 * locale, so this pool is constant rather than locale-keyed.
 */
const LEARN_INTRO_VARIANTS = ['canyoucount', 'howmany', 'toucheach'];

/**
 * Default praise / interjection / failure pools (English).  The active
 * locale's `pools` block in voice-sets.json overrides these per call;
 * the constants are used (a) when voice-sets.json doesn't define a pool
 * for the active locale and (b) as the back-compat fallback for tests
 * that pass FAKE_DATA without a `pools` key.
 *
 * Why the per-locale override exists: ja/ko praise audio is named
 * correct1..correctN, not goodcounting/verygood/youdidit.  Without the
 * locale-specific pool the Learn state machine would request a missing
 * URL after a counting round, the Howl would emit `loaderror`, and the
 * scene would hang waiting for praise that never plays.  See voice-
 * sets.json `_pools_comment` for the full per-locale mapping.
 */
const LEARN_SUCCESS_VARIANTS = ['goodcounting', 'verygood', 'youdidit'];
const INTERJECTION_VARIANTS = ['interjection1', 'interjection2', 'interjection3'];
const QUIZ_SUCCESS_VARIANTS = ['goodcounting', 'verygood', 'youdidit'];
const INCORRECT_GENERIC_VARIANTS = ['failure1', 'failure2', 'failure3', 'failure4'];

/**
 * Default %s template for the "count then touch <object>" assist prompt.
 * Korean overrides this in voice-sets.json to `countthe_%s` because the
 * legacy ko.lproj audio file is named `countthe_<slug>.caf`.
 */
const DEFAULT_PLAYER_ASSIST_TEMPLATE = 'countthentouch_%s';

/**
 * Probability that the quiz-success path plays a silent-pause +
 * interjection instead of the standard praise.  Matches
 * `INTERJECTION_CHANCE_FACTOR = 8` in the legacy spec (§5).
 */
const INTERJECTION_CHANCE_FACTOR = 8;

// ---------------------------------------------------------------------------
// Global sound-effect sequences (shared across themes)
// ---------------------------------------------------------------------------
//
// The legacy plist aliases symbolic names like "pop1" / "plaqueTouch1"
// to real file names.  We bake the resolved file paths in here so
// callers pass an index.  Both arrays are indexed from 0 and return
// silence (no playback) when the index is past the end -- matches the
// spec §10.9 "caller must tolerate silence" contract.
//
// Sources:
//   AppConfiguration.plist:2987-2996  -- pop1..pop5 -> pop2a..pop2e
//   AppConfiguration.plist:3057-3064  -- plaqueTouch1..4 -> playful_reveal_melodic_*
//   AppConfiguration.plist:3158-3172  -- the sequence arrays that drive the indexing

const POP_SEQUENCE_PATHS = Object.freeze([
  'audio/sfx/pop2a.mp3',   // legacy alias: pop1
  'audio/sfx/pop2b.mp3',   // legacy alias: pop2
  'audio/sfx/pop2c.mp3',   // legacy alias: pop3
  'audio/sfx/pop2d.mp3',   // legacy alias: pop4
  'audio/sfx/pop2e.mp3',   // legacy alias: pop5
]);

const PLAQUE_TOUCH_SEQUENCE_PATHS = Object.freeze([
  'audio/sfx/playful_reveal_melodic_01.mp3',  // legacy alias: plaqueTouch1
  'audio/sfx/playful_reveal_melodic_02.mp3',  // legacy alias: plaqueTouch2
  'audio/sfx/playful_reveal_melodic_03.mp3',  // legacy alias: plaqueTouch3
  // plaqueTouch4 deliberately reuses _03 -- the legacy plist entry at
  // line 3063 points plaqueTouch4 at the same file as plaqueTouch3.
  'audio/sfx/playful_reveal_melodic_03.mp3',
]);

/**
 * SFX cue played at the start of the CORRECT_FEEDBACK phase -- the
 * moment the player taps the correct plaque.  Reuses the legacy
 * `learnGameComplete01` sound (the same file that plays when a Learn
 * round's final count lands).  Fired concurrently with the plaque's
 * rotation celebration; resolves before or overlapping the verbal
 * praise (playQuizSuccess).
 */
const QUIZ_CORRECT_REVEAL_PATH = 'audio/sfx/playful_reveal_melodic_04.mp3';

// ---------------------------------------------------------------------------
// Public factory
// ---------------------------------------------------------------------------

/**
 * Create a game-audio helper bound to a locale, voice-sets data blob, and
 * a low-level audio engine.  Returns an object with one method per named
 * sequence from CONVERSION-PLAN section 8.
 *
 * All sequence methods take the active voice set as a parameter (chosen
 * per-scene by the state machine via pickVoiceSetForLocale) plus an
 * optional PlaybackToken.  Each method returns a Promise that resolves
 * when playback finishes -- or immediately when the token is cancelled.
 *
 * @param {Object} opts
 * @param {Object} opts.voiceSetsData  Parsed voice-sets.json.
 * @param {string} opts.locale         Locale key into voiceSetsData (e.g. 'en').
 * @param {(src: string) => Promise<void>} opts.play
 *   Low-level one-shot playback.  Normally `play` from src/audio/engine.js.
 * @param {(seconds: number) => Promise<void>} [opts.pause]
 *   Optional pause helper; defaults to a setTimeout-based sleep.
 * @param {() => number} [opts.rng]    RNG for variant selection.  Defaults to Math.random.
 * @param {() => boolean} [opts.verbalPromptsEnabled]
 *   Probe consulted on every verbal prompt (`playLearnSessionStart`,
 *   `playNewThemeIntro`, `playLearnIntro`, `playLearnSuccess`,
 *   `playInterjection`, `playQuizIntro`, `playQuizSuccess`,
 *   `playIncorrectFeedback`, `playPlayerAssistPrompt`).  When it
 *   returns `false`, the prompt silently no-ops -- caller-side
 *   sequencing still advances.  SFX helpers (`playPlaquePop`,
 *   `playPlaqueTouchPop`, `playQuizCorrectReveal`) ignore this flag
 *   -- they're audio cues tied to tap feedback, not verbal content.
 *   Typically wired to `getVerbalPrompts` from src/settings.js.
 *   Defaults to `() => true` when not supplied so standalone callers
 *   (and existing tests) aren't required to provide one.
 */
export function createGameAudio({
  voiceSetsData,
  locale,
  play,
  pause,
  rng = Math.random,
  verbalPromptsEnabled,
} = {}) {
  if (!voiceSetsData) throw new TypeError('createGameAudio: voiceSetsData required');
  if (!locale) throw new TypeError('createGameAudio: locale required');
  if (typeof play !== 'function') throw new TypeError('createGameAudio: play must be a function');

  const pauseFn = typeof pause === 'function'
    ? pause
    : (seconds) => new Promise((resolve) => setTimeout(resolve, seconds * 1000));

  const verbalOn = typeof verbalPromptsEnabled === 'function'
    ? verbalPromptsEnabled
    : () => true;

  function pickRandom(pool) {
    return pool[Math.floor(rng() * pool.length)] ?? pool[0];
  }

  /**
   * Look up a per-locale prompt-basename pool.  Returns the default
   * (English) pool when the active locale doesn't define one, so callers
   * always get a non-empty array.  Voice-sets.json defines pool keys
   * `praise`, `interjection`, `incorrectGeneric` per locale.
   */
  function getLocalePool(name, defaultPool) {
    const pool = voiceSetsData?.[locale]?.pools?.[name];
    if (Array.isArray(pool) && pool.length > 0) return pool;
    return defaultPool;
  }

  /**
   * Look up the per-locale player-assist prompt template.  Must contain
   * `%s` (replaced with the theme slug).  Falls back to the English
   * default when the active locale doesn't define one.
   */
  function getPlayerAssistTemplate() {
    const tpl = voiceSetsData?.[locale]?.pools?.playerAssistTemplate;
    if (typeof tpl === 'string' && tpl.includes('%s')) return tpl;
    return DEFAULT_PLAYER_ASSIST_TEMPLATE;
  }

  /**
   * Resolve a prompt URL and play it, checking `token.cancelled` after
   * the await.  If the URL can't be resolved (missing file in data), we
   * log a DEV warning and resolve immediately so the caller's sequencing
   * keeps moving -- we never want a missing prompt to wedge the game loop.
   *
   * If `verbalPromptsEnabled()` returns false at call time, this is a
   * silent no-op -- we don't even resolve the URL.
   */
  async function playPrompt(basename, voiceSet, token) {
    if (token?.cancelled) return;
    if (!verbalOn()) return;
    const url = resolvePrompt(voiceSetsData, locale, voiceSet, basename);
    if (!url) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[game-audio] no URL for prompt "${basename}" in locale "${locale}"`);
      }
      return;
    }
    await play(url);
  }

  return {
    /**
     * "Let's learn to count!" -- plays once when the user picks Learn
     * from the home screen, before entering Learn mode.
     */
    async playLearnSessionStart(voiceSet, token) {
      await playPrompt('letslearntocount', voiceSet, token);
    },

    /**
     * "Let's practice counting!" -- plays once when the user picks Quiz
     * from the home screen, before entering Quiz mode.
     */
    async playQuizSessionStart(voiceSet, token) {
      await playPrompt('letspracticecounting', voiceSet, token);
    },

    /**
     * "Let's count the sheep!" -- plays at the start of each round,
     * after SCENE_SETUP picks a theme but before the entrance animation.
     */
    async playNewThemeIntro(themeId, voiceSet, token) {
      const slug = themeToObjectSlug(themeId);
      await playPrompt(`letscount_${slug}`, voiceSet, token);
    },

    /**
     * "Can you count the sheep?" / "How many sheep?" / "Touch each sheep"
     * -- plays after entrance animation, kicks off the COUNTING state.
     * Variant is picked randomly per round.
     */
    async playLearnIntro(themeId, voiceSet, token) {
      const slug = themeToObjectSlug(themeId);
      const variant = pickRandom(LEARN_INTRO_VARIANTS);
      await playPrompt(`${variant}_${slug}`, voiceSet, token);
    },

    /**
     * Random praise clip ("Good counting!" / "Very good!" / "You did it!"
     * in en/zh-CN; "correct1/2/3.mp3" in ja/ko)  -- plays when all
     * objects have been counted.
     */
    async playLearnSuccess(voiceSet, token) {
      const variant = pickRandom(getLocalePool('praise', LEARN_SUCCESS_VARIANTS));
      await playPrompt(variant, voiceSet, token);
    },

    /**
     * Short "filler" interjection between rounds.  Per plan section 8:
     * "Random fun interjection with 0.5s pause".  The pause runs even if
     * the interjection resolves instantly (missing file); the caller can
     * still cancel via token.  Locale-specific pool (ja ships 5
     * interjections vs en's 3).
     */
    async playInterjection(voiceSet, token) {
      const variant = pickRandom(getLocalePool('interjection', INTERJECTION_VARIANTS));
      await playPrompt(variant, voiceSet, token);
      if (token?.cancelled) return;
      await pauseFn(0.5);
    },

    // ---------------------------------------------------------------------
    // Quiz-mode voice prompts (QUIZ-MODE-LEGACY-SPEC §5)
    // ---------------------------------------------------------------------

    /**
     * "How many sheep do you see?" -- plays at the start of each quiz
     * round after the entrance animation finishes and before the choice
     * plaques enter.
     *
     * QUIZ-MODE-LEGACY-SPEC §5 `quizGameIntro` describes this as a
     * randomSequence wrapping learnGameIntro01 -> objectName step.  In
     * the legacy plist the randomSequence has exactly ONE member
     * (learnGameIntro01) and learnGameIntro01's objectName step maps
     * both singular and plural to the same `howmany_%@.caf` file, so
     * the randomSequence + objectName indirection is a no-op today.
     * We call howmany_<slug> directly instead.
     *
     * Bring the randomSequence wrapper back ONLY IF a locale registers
     * sibling howmany* entries -- e.g., Japanese or Korean adding
     * `howmany01_sheep` + `howmany02_sheep` so the game rotates between
     * greeting variants.  The restoration is a one-liner:
     *
     *   await playPrompt(
     *     pickRandom([`howmany01_${slug}`, `howmany02_${slug}`]),
     *     voiceSet, token,
     *   );
     *
     * `objectCount` is likewise reserved for locales whose singular/
     * plural audio differs (e.g. a future Japanese counter-word
     * variation).  English has no such split so the parameter is
     * accepted but unused.
     *
     * @param {string} themeId
     * @param {Object} voiceSet
     * @param {number} _objectCount  Reserved for i18n; currently unused.
     * @param {Object} [token]
     */
    async playQuizIntro(themeId, voiceSet, _objectCount, token) {
      const slug = themeToObjectSlug(themeId);
      await playPrompt(`howmany_${slug}`, voiceSet, token);
    },

    /**
     * Correct-answer celebration praise.  1-in-`INTERJECTION_CHANCE_FACTOR`
     * (= 1/8) chance to replace the praise clip with an inter-scene
     * interjection (silent pause 0.5 s + random interjection1..3) --
     * matches the legacy `quizCountSuccess` branching (§5 + §2).
     *
     * The interjection branch plays the pause BEFORE the clip (spec
     * §5 `interSceneInterjection` shape: `silentPause 0.5 s` then
     * `randomPlaySoundList`), while the praise branch is a single clip.
     */
    async playQuizSuccess(voiceSet, token) {
      if (rng() < 1 / INTERJECTION_CHANCE_FACTOR) {
        if (token?.cancelled) return;
        await pauseFn(0.5);
        if (token?.cancelled) return;
        const variant = pickRandom(getLocalePool('interjection', INTERJECTION_VARIANTS));
        await playPrompt(variant, voiceSet, token);
        return;
      }
      // Legacy iOS aliases quizCountSuccess -> learnGameSuccess01, so we
      // share the same `praise` pool for both Learn and Quiz success.
      const variant = pickRandom(getLocalePool('praise', QUIZ_SUCCESS_VARIANTS));
      await playPrompt(variant, voiceSet, token);
    },

    /**
     * Wrong-answer feedback.  Implements the legacy `incorrectChoicePlaque`
     * randomSequence (§5):
     *
     *   Branch 50% -> incorrectChoice01: random failure1..failure4
     *   Branch 50% -> incorrectChoice02: comparator relativeSequence
     *       chosen <  correct: "there are MORE <object>s than that"
     *       chosen >  correct: "there are FEWER <object>s than that"
     *       chosen == correct: falls back to incorrectChoice01 (this
     *                          shouldn't fire because the correct-choice
     *                          path would have run; handled defensively).
     *
     * @param {string} themeId
     * @param {number} correctValue
     * @param {number} chosenValue
     * @param {Object} voiceSet
     * @param {Object} [token]
     */
    async playIncorrectFeedback(themeId, correctValue, chosenValue, voiceSet, token) {
      const pickGeneric = rng() < 0.5 || chosenValue === correctValue;
      if (pickGeneric) {
        const variant = pickRandom(getLocalePool('incorrectGeneric', INCORRECT_GENERIC_VARIANTS));
        await playPrompt(variant, voiceSet, token);
        return;
      }
      const slug = themeToObjectSlug(themeId);
      const basename = chosenValue < correctValue
        ? `therearemore_${slug}`
        : `therearefewer_${slug}`;
      await playPrompt(basename, voiceSet, token);
    },

    /**
     * "Count the sheep, then touch the right number" -- fires after 30 s
     * of idle with un-tapped plaques remaining (spec §5
     * `quizPlayerPausePrompt`).  `objectCount` is passed for future i18n
     * (Japanese counter words) but the English file is theme-only.
     *
     * @param {string} themeId
     * @param {Object} voiceSet
     * @param {number} _objectCount  Reserved for i18n; currently unused.
     * @param {Object} [token]
     */
    async playPlayerAssistPrompt(themeId, voiceSet, _objectCount, token) {
      const slug = themeToObjectSlug(themeId);
      // Korean overrides the template to `countthe_%s` (legacy ko.lproj
      // ships countthe_<slug>.caf, not countthentouch_<slug>.caf).
      const tpl = getPlayerAssistTemplate();
      await playPrompt(tpl.replace('%s', slug), voiceSet, token);
    },

    /**
     * Entrance-order pop for the Nth choice plaque to appear.  Indexed
     * from 0.  Past the end of POP_SEQUENCE_PATHS the call resolves
     * silently (spec §10.9 "caller must tolerate silence") -- no theme
     * ships more than 4 plaques today so index 5+ isn't expected.
     *
     * @param {number} index     Zero-based entrance order.
     * @param {Object} [token]
     */
    async playPlaquePop(index, token) {
      if (token?.cancelled) return;
      if (!Number.isInteger(index) || index < 0) return;
      const path = POP_SEQUENCE_PATHS[index];
      if (!path) return;
      await play('/' + path);
    },

    /**
     * Correct-answer reveal cue -- plays the legacy
     * `learnGameComplete01` SFX (playful_reveal_melodic_04.mp3) the
     * moment the player taps the correct choice plaque.  Fire-and-
     * forget: callers don't await.  The rotation celebration + voice
     * praise run concurrently / afterward.
     *
     * @param {Object} [token]
     */
    async playQuizCorrectReveal(token) {
      if (token?.cancelled) return;
      await play('/' + QUIZ_CORRECT_REVEAL_PATH);
    },

    /**
     * Plaque-touch pop for the Nth plaque-touch event within a round.
     * Indexed from 0.  The legacy `_quizGamePlaqueTouchCounter` caps
     * at `plaqueLocations.count`, so callers should cap the counter
     * before invoking; indices past the end resolve silently.
     *
     * Entry 3 deliberately reuses the same file as entry 2 (plaqueTouch4
     * → plaqueTouch3's file in the legacy plist).
     *
     * @param {number} counter   Zero-based touch counter.
     * @param {Object} [token]
     */
    async playPlaqueTouchPop(counter, token) {
      if (token?.cancelled) return;
      if (!Number.isInteger(counter) || counter < 0) return;
      const path = PLAQUE_TOUCH_SEQUENCE_PATHS[counter];
      if (!path) return;
      await play('/' + path);
    },
  };
}

// ---------------------------------------------------------------------------
// Test hooks
// ---------------------------------------------------------------------------

/** @internal -- exposed for unit tests only. */
export const __TEST__ = {
  LEARN_INTRO_VARIANTS,
  LEARN_SUCCESS_VARIANTS,
  INTERJECTION_VARIANTS,
  QUIZ_SUCCESS_VARIANTS,
  INCORRECT_GENERIC_VARIANTS,
  INTERJECTION_CHANCE_FACTOR,
  POP_SEQUENCE_PATHS,
  PLAQUE_TOUCH_SEQUENCE_PATHS,
  QUIZ_CORRECT_REVEAL_PATH,
};
